'use client';
import cn from 'classnames';
import Image from 'next/image';
import { useState, useEffect } from 'react';

import WordmarkWhite from 'public/img/logo/vivrelle-wordmark-white.svg';
import Instagram from 'public/img/icon/instagram-footer.svg';
import TikTok from 'public/img/icon/tiktok-footer.svg';

import './Footer.style.scss';

import FooterNewsletterSignupForm from '@/components/form/FooterNewsletterSignupForm/FooterNewsletterSignupForm';
import { useLoggedInUser } from '@/components/hook/user';
import { SIGNUP_ROUTES } from '@/const/signup';
import Text from '@/components/common/Text/Text';
import VivrelleFlowerWhite from 'public/img/logo/vivrelle-flower-white.png';

type FooterProps = {
  className?: string;
};

function Footer(props: FooterProps) {
  const { className = '' } = props;
  const user = useLoggedInUser();
  const userWayKey = process.env.NEXT_PUBLIC_USERWAY_KEY || '';
  const currentYear = new Date().getFullYear();
  const [cookieYesActive, setCookieYesActive] = useState(false);

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_COOKIEYES_ID) return;
    // Returning visitors: check the consent cookie directly — more reliable than
    // window.getCkyConsent which only initialises on hard load, not SPA navigation.
    // action:yes means the user was in a consent-required region and explicitly
    // made a choice. action:no means CookieYes auto-set the cookie for a
    // non-required region (e.g. NY) — hide the link in that case.
    if (
      document.cookie.includes('cookieyes-consent') &&
      document.cookie.includes('action:yes')
    ) {
      setCookieYesActive(true);
      return;
    }
    // New visitors: show the link once they interact with the banner.
    const handler = () => setCookieYesActive(true);
    document.addEventListener('cookieyes-consent-update', handler);
    return () =>
      document.removeEventListener('cookieyes-consent-update', handler);
  }, []);

  return (
    // Originally, all the links in the Footer were using our NextLink component.
    // These were changed to use HTML <a> so that when a user leaves the sign up
    // page before completing the registration, we can prompt them to warn them
    // that they will lose the data the inputed.

    // At the time of writing this, NextJS doesn't have an easy way to intercept
    // route change with a prompt. We're sacrificing prefecthing routes for the above
    <div className={cn('Footer', className)}>
      <div className='Footer-logo'>
        <Image
          className='Footer-logo-svg'
          src={WordmarkWhite}
          height={25}
          width={145}
          alt='Vivrelle wordmark in white'
        />
      </div>
      <div className='Footer-content'>
        <div className='Footer-menu'>
          <a
            className='Link Footer-menu-item link-m uppercase'
            href='/'
          >
            Home
          </a>

          <a
            className='Link Footer-menu-item link-m uppercase'
            href='/how-it-works'
          >
            How It Works
          </a>

          <a
            className='Link Footer-menu-item link-m uppercase'
            href='/perks'
          >
            Club Perks
          </a>

          {!user && (
            <a
              className='Link Footer-menu-item link-m uppercase'
              href={SIGNUP_ROUTES.ACCOUNT}
            >
              Apply now
            </a>
          )}

          <a
            className='Link Footer-menu-item link-m uppercase'
            href='https://shop.vivrelle.com'
          >
            Merch Shop
          </a>
        </div>
        <div className='Footer-links'>
          <div className='Footer-links-column'>
            {/* 
              Replicating what we're doing in the main nav.

              Using regular links here because if the user is on the PLP, the query string changes, but the page itself won't update since it's higher in the component tree than the PLP page. 

              These links technically slow the page down and prevent pre-loading,
              but it will perform a full refresh of the page on click, which is OK.
              Functionality over framework
            */}
            <a
              className='Link Footer-menu-item link-m uppercase'
              href='/collection'
            >
              Our Collection
            </a>
            <a
              className='Link Footer-menu-item link-m uppercase'
              href='/collection?collection=Handbags'
            >
              Handbags
            </a>
            <a
              className='Link Footer-menu-item link-m uppercase'
              href='/collection?collection=Designer%20Jewelry'
            >
              Designer Jewelry
            </a>
            <a
              className='Link Footer-menu-item link-m uppercase'
              href='/collection?collection=Fine%20Jewelry'
            >
              Fine Jewelry
            </a>
          </div>
          <div className='Footer-links-column'>
            {user ? (
              <a
                className='Link Footer-menu-item link-m uppercase'
                href='/account'
              >
                My Account
              </a>
            ) : (
              <a
                className='Link Footer-menu-item link-m uppercase'
                href='/gift-cards'
              >
                Gift Cards
              </a>
            )}
            <a
              className='Link Footer-menu-item link-m uppercase'
              href='/faqs'
            >
              FAQ
            </a>

            <a
              className='Link Footer-menu-item link-m uppercase'
              href='/contact-us'
            >
              Contact Us
            </a>

            <a
              className='Link Footer-menu-item link-m uppercase'
              href='https://jobs.lever.co/vivrelle'
            >
              Careers
            </a>
          </div>
        </div>

        <div className='Footer-newsletter'>
          <h3 className='Footer-newsletter-title'>Stay in the Know</h3>
          <p className='Footer-newsletter-message'>
            Join our mailing list to stay up to date on the latest trends and
            new arrivals.
          </p>
          <FooterNewsletterSignupForm initialValues={{ email: '' }} />

          <div className='Footer-social'>
            <a
              className='Link link-m'
              href='https://www.instagram.com/vivrelle'
            >
              <Image
                className='social-icon'
                src={Instagram}
                alt='Instagram account'
              />
            </a>
            <a
              className='Link link-m'
              href='https://www.tiktok.com/@vivrelle'
            >
              <Image
                className='social-icon'
                src={TikTok}
                alt='TikTok account'
              />
            </a>
          </div>
        </div>
      </div>
      <div className='Footer-policy'>
        <div className='Footer-policy-disclaimer'>
          © Vivrelle {currentYear}. Vivrelle is not an authorized provider nor
          affiliated with any third-party brand. Brands are not responsible for
          any product on the website. Vivrelle guarantees the authenticity of
          goods offered—see our{' '}
          <a
            className='Link Footer-menu-item link-m uppercase'
            href='/faqs'
          >
            FAQs
          </a>{' '}
          for more details.
        </div>
        <div className='Footer-policy-links'>
          <a
            className='Link link-m uppercase'
            href='/privacy-policy'
          >
            Privacy Policy
          </a>

          <a
            className='Link link-m uppercase'
            href='/terms'
          >
            Terms & Conditions
          </a>

          {cookieYesActive && (
            <a
              className='Link link-m uppercase cky-banner-element'
              href='#'
            >
              {/* CPRA opt-out icon — required alongside "Your Privacy Choices" label */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src='/img/icon/privacy-choices.png'
                alt=''
                aria-hidden='true'
                style={{
                  display: 'inline',
                  verticalAlign: 'middle',
                  marginRight: '5px',
                  height: '1em',
                  width: 'auto'
                }}
              />
              Your Privacy Choices
            </a>
          )}
        </div>
      </div>

      <div className='Footer-flower-logo-container'>
        <Image
          src={VivrelleFlowerWhite}
          height={80}
          width={80}
          alt='Vivrelle flower logo in white'
          priority
        />
      </div>

      <script
        src='https://cdn.userway.org/widget.js'
        data-account={userWayKey}
        defer
      />
    </div>
  );
}

export default Footer;
